import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import DecisionBrief from './DecisionBrief';
import PlatformReportPanel from './PlatformReportPanel';
import QualityInsights from './QualityInsights';
import ReportTextPanel from './ReportTextPanel';
import RunSummaryAgentDrilldown from './RunSummaryAgentDrilldown';
import RunSummaryAgentGraphSection from './RunSummaryAgentGraphSection';
import RunSummaryAgentList from './RunSummaryAgentList';
import StatusPanel from './StatusPanel';
import TriagePriorities from './TriagePriorities';
import { runSummaryAgentReportStyles } from './styles/runSummaryAgentReportStyles';

export default function RunSummaryAgentReport({
  disabled,
  error,
  graphics,
  loading,
  onGenerate,
  report,
  visuals = {
    versions: [],
    flowScenarioDrilldown: [],
  },
}) {
  const [selectedTab, setSelectedTab] = useState('general');
  const remainingRate = Math.max(0, 100 - graphics.completionRate - graphics.failureRate);
  const androidReport = report?.platformReports?.android || {};
  const iosReport = report?.platformReports?.ios || {};
  const trendReport = report?.trendReport || {};
  const releaseConfidence = report?.releaseConfidence || {};
  const topLine = report?.topLine || {};
  const handleTabChange = (_event, value) => {
    setSelectedTab(value);
  };

  return (
    <Paper variant="outlined" sx={runSummaryAgentReportStyles.root}>
      <Box sx={runSummaryAgentReportStyles.header}>
        <Box>
          <Box sx={runSummaryAgentReportStyles.titleRow}>
            <Box sx={runSummaryAgentReportStyles.titleIcon}>
              <AutoAwesomeIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={runSummaryAgentReportStyles.title}>
                Run Summary Agent
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={onGenerate}
          disabled={disabled || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          sx={runSummaryAgentReportStyles.button}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={runSummaryAgentReportStyles.alert}>
          {error}
        </Alert>
      )}

      <Box sx={runSummaryAgentReportStyles.graphicsGrid}>
        <Box sx={runSummaryAgentReportStyles.graphicCard}>
          <Typography variant="caption" color="text.secondary">
            Completion Rate
          </Typography>
          <Typography variant="h5" sx={runSummaryAgentReportStyles.graphicValue('success.main')}>
            {graphics.completionRate}%
          </Typography>
        </Box>
        <Box sx={runSummaryAgentReportStyles.graphicCard}>
          <Typography variant="caption" color="text.secondary">
            Failure Rate
          </Typography>
          <Typography variant="h5" sx={runSummaryAgentReportStyles.graphicValue('error.main')}>
            {graphics.failureRate}%
          </Typography>
        </Box>
        <Box sx={runSummaryAgentReportStyles.graphicCard}>
          <Typography variant="caption" color="text.secondary">
            Most Failed Flow
          </Typography>
          <Typography
            variant="body1"
            noWrap
            sx={runSummaryAgentReportStyles.graphicValue('primary.main')}
          >
            {graphics.mostFailedFlow}
          </Typography>
        </Box>
        <Box sx={runSummaryAgentReportStyles.graphicCard}>
          <Typography variant="caption" color="text.secondary">
            Most Failed Scenario
          </Typography>
          <Typography
            variant="body1"
            noWrap
            sx={runSummaryAgentReportStyles.graphicValue('primary.main')}
          >
            {graphics.mostFailedScenario}
          </Typography>
        </Box>
      </Box>

      <Box sx={runSummaryAgentReportStyles.barTrack}>
        <Box sx={runSummaryAgentReportStyles.barSegment(graphics.completionRate, 'success.main')} />
        <Box sx={runSummaryAgentReportStyles.barSegment(graphics.failureRate, 'error.main')} />
        <Box sx={runSummaryAgentReportStyles.barSegment(remainingRate, 'custom.terminalBg')} />
      </Box>

      {report ? (
        <Box sx={runSummaryAgentReportStyles.reportTabsRoot}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={runSummaryAgentReportStyles.reportTabs}
          >
            <Tab label="General" value="general" />
            {visuals?.ios && <Tab label="iOS" value="ios" />}
            {visuals?.android && <Tab label="Android" value="android" />}
          </Tabs>

          {selectedTab === 'general' && (
            <Box sx={runSummaryAgentReportStyles.reportBody}>
              <DecisionBrief fallbackHeadline={report.headline} topLine={topLine} />

              {report.summary && (
                <ReportTextPanel title="Short Explanation">{report.summary}</ReportTextPanel>
              )}

              <Box sx={runSummaryAgentReportStyles.trendGrid}>
                <StatusPanel
                  title="Release Confidence"
                  chipLabel={releaseConfidence.level}
                  body={
                    releaseConfidence.reasoning ||
                    releaseConfidence.decisionSupport ||
                    'No release confidence available.'
                  }
                />
                <StatusPanel
                  title="Trend Report"
                  chipLabel={trendReport.status}
                  body={trendReport.summary}
                />
              </Box>
              <TriagePriorities items={report.triagePriorities} />
              <QualityInsights items={report.qualityInsights?.slice(0, 3)} />

              <Box sx={runSummaryAgentReportStyles.trendGrid}>
                <Box sx={runSummaryAgentReportStyles.reportPanel}>
                  <Typography variant="subtitle2" sx={runSummaryAgentReportStyles.sectionTitle}>
                    Key Risks
                  </Typography>
                  <RunSummaryAgentList items={report.riskAreas} />
                </Box>
                <Box sx={runSummaryAgentReportStyles.reportPanel}>
                  <Typography variant="subtitle2" sx={runSummaryAgentReportStyles.sectionTitle}>
                    Next Actions
                  </Typography>
                  <RunSummaryAgentList items={report.recommendedActions} />
                </Box>
              </Box>

              <Box sx={runSummaryAgentReportStyles.supportingSection}>
                <Typography variant="subtitle1" sx={runSummaryAgentReportStyles.sectionTitle}>
                  Supporting Details
                </Typography>
                <Box sx={runSummaryAgentReportStyles.trendGrid}>
                  <ReportTextPanel title="Manager Takeaway">
                    {report.managerTakeaway || 'No manager takeaway available.'}
                  </ReportTextPanel>
                  <ReportTextPanel title="Platform Comparison">
                    {report.platformComparison}
                  </ReportTextPanel>
                </Box>
                {releaseConfidence.decisionSupport && (
                  <ReportTextPanel title="Decision Support">
                    {releaseConfidence.decisionSupport}
                  </ReportTextPanel>
                )}
                {trendReport.signals?.length > 0 && (
                  <Box sx={runSummaryAgentReportStyles.reportPanel}>
                    <Typography variant="subtitle2" sx={runSummaryAgentReportStyles.sectionTitle}>
                      Trend Signals
                    </Typography>
                    <RunSummaryAgentList items={trendReport.signals} />
                  </Box>
                )}
                <Box sx={runSummaryAgentReportStyles.graphGrid}>
                  <RunSummaryAgentGraphSection
                    title="Versions"
                    subtitle="Top version outcome mix"
                    items={visuals.versions}
                  />
                </Box>
                <RunSummaryAgentDrilldown items={visuals.flowScenarioDrilldown} />
              </Box>

              {report.dataCaveats?.length > 0 && (
                <Box sx={runSummaryAgentReportStyles.reportPanel}>
                  <Typography variant="subtitle2" sx={runSummaryAgentReportStyles.sectionTitle}>
                    Data Caveats
                  </Typography>
                  <RunSummaryAgentList items={report.dataCaveats} />
                </Box>
              )}
            </Box>
          )}

          {selectedTab === 'ios' && (
            <PlatformReportPanel
              platform="iOS"
              description="View iOS-specific failed flows, affected scenarios, risks, and recommended actions."
              items={visuals.ios}
              drilldownProps={{ iosSummary: iosReport.summary }}
              keyRisks={iosReport.keyRisks}
              recommendedActions={iosReport.recommendedActions}
            />
          )}

          {selectedTab === 'android' && (
            <PlatformReportPanel
              platform="Android"
              description="View Android-specific failed flows, affected scenarios, risks, and recommended actions."
              items={visuals.android}
              drilldownProps={{ androidSummary: androidReport.summary }}
              keyRisks={androidReport.keyRisks}
              recommendedActions={androidReport.recommendedActions}
            />
          )}
        </Box>
      ) : (
        <Box sx={runSummaryAgentReportStyles.emptyState}>
          <Typography variant="body2">
            Press Generate Report to see a short visual overview for the selected filters.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
